import { useState } from "react"

import { App, Form } from "@/atoms"
import { hasTicketProjects } from "@/domain/ticketing"
import {
  getTicketError,
  getUnavailableVendorMessage
} from "@/helpers/error.helper"
import { buildTicketFields } from "@/helpers/ticketing.helpers"
import { useTranslation } from "@/hooks/useTranslation"
import { processValidationErrors } from "@/organisms/Ticketing/components/JiraTemplateModal/helpers"
import { TicketFields } from "@/organisms/Ticketing/components/TicketField"
import { TicketModal } from "@/organisms/Ticketing/components/TicketModal"
import { useTicketRepository } from "@/organisms/Ticketing/hooks/useTicketRepository"
import { useVendorTicketingMetadata } from "@/organisms/Ticketing/hooks/useVendorTicketingMetadata"
import { CreatedTicketMessage } from "./CreatedTicketMessage"

import type { TicketFormValues } from "@/helpers/ticketing.helpers.types"
import type { TicketIssueModalProps } from "./TicketIssueModal.types"

const SUCCESS_DURATION = 10

export const TicketIssueModal = (props: TicketIssueModalProps) => {
  const { system, payload, closeModal, onCreate } = props
  const { t } = useTranslation("translation", { keyPrefix: "ticketModal" })
  const { message } = App.useApp()
  const [form] = Form.useForm<TicketFormValues>()
  const [formError, setFormError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const repository = useTicketRepository(system, payload.id)
  const metadata = useVendorTicketingMetadata(system, form, {
    repository: repository.repository
  })
  const repositoryError = repository.isMissing
    ? t("error.noGitHubRepository")
    : null
  const { projectId, projects, types, fields, url } = metadata
  const [initialValues] = useState(() => ({
    title: payload.summary,
    description: payload.description
  }))

  const onSubmit = async () => {
    try {
      await form.validateFields()
    } catch {
      setFormError(t("error.completeRequiredFields"))
      return
    }

    setFormError(null)
    setCreating(true)

    try {
      const ticket = await onCreate({
        provider: system,
        fields: buildTicketFields(fields, form.getFieldsValue(true))
      })

      message.success({
        content: <CreatedTicketMessage ticket={ticket} />,
        duration: SUCCESS_DURATION
      })
    } catch (error) {
      const ticketError = getTicketError(error)

      if (!ticketError) {
        setFormError(
          getUnavailableVendorMessage(error) ?? t("error.creationFailed")
        )
        return
      }

      const { formErrors, fieldErrors } = processValidationErrors(
        ticketError,
        t
      )

      form.setFields(
        Object.entries(fieldErrors).map(([name, errorMessage]) => ({
          name,
          errors: [errorMessage]
        }))
      )
      setFormError(formErrors.join("; ") || t("error.creationFailed"))
    } finally {
      setCreating(false)
    }
  }

  return (
    <TicketModal
      system={system}
      type={system}
      mode="ticket"
      form={form}
      initialValues={initialValues}
      fixedProjectLabel={
        hasTicketProjects(system) ? undefined : t("repositoryField")
      }
      url={url}
      projects={projects}
      issueTypesOptions={types}
      metadataIsLoading={metadata.metadataIsLoading || repository.isLoading}
      fieldsIsLoading={metadata.fieldsIsLoading}
      formError={
        formError ?? repositoryError ?? metadata.unavailableMessage ?? null
      }
      initialSummary={payload.summary}
      initialDescription={payload.description}
      submitDisabled={
        creating ||
        metadata.fieldsIsLoading ||
        repository.isLoading ||
        !!repositoryError ||
        metadata.isRefused
      }
      onCancel={closeModal}
      onSubmit={onSubmit}
    >
      <TicketFields
        system={system}
        mode="ticket"
        form={form}
        projectId={projectId}
        fields={fields}
      />
    </TicketModal>
  )
}
